import { useMutation, useQuery } from '@tanstack/react-query'
import { BookingState, BookingStateUrlParameter } from '@xeetr/types/enums'
import {
  BaseBookingModel,
  BookingStateInfo,
  FoodBookingModel,
  FoodBookingSeriesResult,
  PostFoodBookingModel,
  PostFoodBookingSeriesModel,
  PostServiceBookingModel,
  ServiceBookingModel,
} from '@xeetr/types/models'
import { useAtomValue } from 'jotai'
import { useContext } from 'react'

import { bookingQueryParamsUrlAtom } from '../atoms/bookings'
import { ActiveTenantContext } from '../context/common/Context'
import {
  invalidateBookingQueries,
  invalidateCustomerChangelogQueries,
} from '../lib/mutationHelpers'
import { queryKeys } from '../lib/queryKeys'
import { BookingService } from '../services/Bookings.service'

import { useErrorHandler } from './common/useErrorHandler'
import { useTenantOptionalService } from './common/useServiceFactory'

import { BookingStateOption } from '@/constants/bookingStates'
import {
  isInsufficientPermissionsError,
  showErrorToastUnlessPermissionDenied,
  showToast,
} from '@/utils/toastMessage'
import { createBookingWithDedupe } from '@/utils/bookingDedupe'

type SingleBookingModel = FoodBookingModel | ServiceBookingModel

interface UseBookingsReturn {
  bookings: BaseBookingModel[]
  isLoading: boolean
  isError: boolean
  error: Error | null

  state: BookingStateOption['value']
  from: Date
  until: Date | null

  create: (
    booking: PostFoodBookingModel | PostServiceBookingModel,
    mail: boolean,
    state?: BookingState,
    callback?: () => void
  ) => void
  createSeries: (
    booking: PostFoodBookingSeriesModel,
    mail: boolean,
    callback?: (result: FoodBookingSeriesResult) => void
  ) => void
  update: (
    booking: PostFoodBookingModel | PostServiceBookingModel,
    mail: boolean,
    scope?: 'occurrence' | 'future',
    callback?: (result?: SingleBookingModel) => void,
    options?: { state?: BookingState }
  ) => void
  updateState: (
    bookingId: string,
    newState: BookingStateUrlParameter,
    info?: BookingStateInfo & { sendEmail?: boolean },
    scope?: 'occurrence' | 'future',
    callback?: (booking: SingleBookingModel) => void
  ) => void
  remove: (bookingId: string, scope?: 'occurrence' | 'future', callback?: () => void) => void

  isCreating: boolean
  isCreatingSeries: boolean
  isUpdating: boolean
  isChangingState: boolean
  isDeleting: boolean
}

export const useBookings = (): UseBookingsReturn => {
  const { tenantId, tenant } = useContext(ActiveTenantContext)

  const queryParams = useAtomValue(bookingQueryParamsUrlAtom)

  // Use the derived queryParams as the source of truth for filter values
  const { state, from, until } = queryParams

  const getService = useTenantOptionalService(BookingService)

  const bookingsQuery = useQuery({
    queryKey: queryKeys.bookings.list(tenantId || '', queryParams),
    queryFn: async () => {
      if (!tenantId || !tenant) return []
      const service = await getService()
      return service.getBookings(
        queryParams.from,
        queryParams.until,
        queryParams.state,
        tenant.timezone
      )
    },
    enabled: !!tenantId && !!tenant,
    staleTime: 30 * 1000,
    refetchInterval: (tenant?.refreshDuration || 300) * 1000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 2,
  })

  useErrorHandler(bookingsQuery.isError, 'Fehler beim Laden der Buchungen.')

  const createMutation = useMutation({
    mutationFn: async ({
      booking,
      mail,
      state,
      requestId,
    }: {
      booking: PostFoodBookingModel | PostServiceBookingModel
      mail: boolean
      state?: BookingState
      requestId?: string
    }) => {
      return createBookingWithDedupe(async () => {
        const service = await getService()
        return service.newBooking(booking, { mail, state }, requestId)
      }, requestId)
    },
    onSuccess: () => {
      if (!tenantId) return
      invalidateBookingQueries(tenantId)
      showToast({ severity: 'success', detail: 'Neue Reservierung erfolgreich erstellt!' })
    },
    onError: (error: any) => {
      console.error(error)

      if (isInsufficientPermissionsError(error)) return

      // Handle specific booking conflict errors
      if (error?.response?.status === 409 && error?.response?.data?.message) {
        showToast({
          severity: 'error',
          detail: error.response.data.message,
          summary: 'Reservierungskonflikt',
        })
        return
      }

      // Handle other API errors
      if (error?.response?.data?.message) {
        showToast({
          severity: 'error',
          detail: error.response.data.message,
        })
        return
      }

      // Fallback for unknown errors
      showToast({ severity: 'error', detail: 'Ein unerwarteter Fehler ist aufgetreten' })
    },
  })

  const createSeriesMutation = useMutation({
    mutationFn: async ({
      booking,
      mail,
      requestId,
    }: {
      booking: PostFoodBookingSeriesModel
      mail: boolean
      requestId?: string
    }) => {
      const service = await getService()
      return service.newBookingSeries(booking, mail, requestId)
    },
    onSuccess: (result) => {
      if (!tenantId) return
      invalidateBookingQueries(tenantId)
      if (result.skipped.length > 0) {
        showToast({
          severity: 'warn',
          summary: 'Serie teilweise erstellt',
          detail: `${result.created.length} von ${result.created.length + result.skipped.length} Terminen erstellt, ${result.skipped.length} wegen Konflikten übersprungen.`,
        })
      } else {
        showToast({
          severity: 'success',
          detail: `${result.created.length} Termine der Serie erfolgreich erstellt!`,
        })
      }
    },
    onError: (error) => {
      console.error(error)
      showErrorToastUnlessPermissionDenied(error, { severity: 'error' })
    },
  })

  const updateMutation = useMutation({
    mutationFn: async ({
      booking,
      mail,
      scope,
      state,
    }: {
      booking: PostFoodBookingModel | PostServiceBookingModel
      mail: boolean
      scope?: 'occurrence' | 'future'
      state?: BookingState
    }) => {
      const service = await getService()
      return service.putBooking(mail, booking, scope, state)
    },
    onSuccess: (data, { state }) => {
      if (!tenantId) return
      const bookingId = 'id' in data ? data.id : undefined
      invalidateBookingQueries(tenantId)
      if (bookingId) {
        invalidateCustomerChangelogQueries(bookingId)
      }
      showToast({
        severity: 'success',
        detail: state ? 'Status erfolgreich geändert' : 'Reservierung erfolgreich gespeichert!',
      })
    },
    onError: (error) => {
      console.error(error)
      showErrorToastUnlessPermissionDenied(error, { severity: 'error' })
    },
  })

  const updateStateMutation = useMutation({
    mutationFn: async ({
      bookingId,
      newState,
      info,
      scope,
    }: {
      bookingId: string
      newState: BookingStateUrlParameter
      info?: BookingStateInfo & { sendEmail?: boolean }
      scope?: 'occurrence' | 'future'
    }) => {
      const service = await getService()
      return service.changeBookingState(
        bookingId,
        newState,
        info?.reason,
        info?.comment,
        info?.sendEmail,
        scope
      )
    },
    onSuccess: (data, variables) => {
      if (!tenantId) return
      invalidateBookingQueries(tenantId)
      const booking = Array.isArray(data) ? data[0] : data
      const bookingId = variables?.bookingId || booking?.id
      if (bookingId) {
        invalidateCustomerChangelogQueries(bookingId)
      }
      const count = Array.isArray(data) ? data.length : 1
      showToast({
        severity: 'success',
        detail:
          count > 1
            ? `Status für ${count} Termine erfolgreich geändert`
            : 'Status erfolgreich geändert',
      })
    },
    onError: (error) => {
      console.error(error)
      showErrorToastUnlessPermissionDenied(error, { severity: 'error' })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async ({
      bookingId,
      scope,
    }: {
      bookingId: string
      scope?: 'occurrence' | 'future'
    }) => {
      const service = await getService()
      return service.deleteBooking(bookingId, scope)
    },
    onSuccess: () => {
      if (!tenantId) return
      invalidateBookingQueries(tenantId)
      showToast({ severity: 'success', detail: 'Reservierung wurde erfolgreich gelöscht!' })
    },
    onError: (error) => {
      console.error(error)
      showErrorToastUnlessPermissionDenied(error, { severity: 'error' })
    },
  })

  return {
    bookings: bookingsQuery.data || [],
    isLoading: bookingsQuery.isLoading,
    isError: bookingsQuery.isError,
    error: bookingsQuery.error,

    state,
    from,
    until,

    create: (booking, mail, state, callback) => {
      const requestId = `booking-create-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      createMutation.mutate({ booking, mail, state, requestId }, { onSuccess: callback })
    },
    createSeries: (booking, mail, callback) => {
      const requestId = `booking-series-create-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      createSeriesMutation.mutate({ booking, mail, requestId }, { onSuccess: callback })
    },
    update: (booking, mail, scope, callback, options) => {
      updateMutation.mutate(
        { booking, mail, scope, state: options?.state },
        // A "this and future" series edit returns a summary instead of a booking.
        { onSuccess: (data) => callback?.('id' in data ? data : undefined) }
      )
    },
    updateState: (bookingId, newState, info, scope, callback) => {
      updateStateMutation.mutate(
        { bookingId, newState, info, scope },
        {
          onSuccess: (data) => {
            const booking = Array.isArray(data) ? data[0] : data
            if (callback && booking) callback(booking)
          },
        }
      )
    },
    remove: (bookingId, scope, callback) => {
      deleteMutation.mutate({ bookingId, scope }, { onSuccess: callback })
    },

    isCreating: createMutation.isPending,
    isCreatingSeries: createSeriesMutation.isPending,
    isUpdating: updateMutation.isPending,
    isChangingState: updateStateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  }
}
